import React, { useState, useEffect } from 'react';
import {
  Button,
  Typography,
  Box,
  Divider,
  Chip,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Paper,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Avatar,
  Stack,
  Card,
  Tabs,
  Tab,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMore from '@mui/icons-material/ExpandMore';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import IconButton from '@mui/material/IconButton';
import AssignmentIcon from '@mui/icons-material/Assignment';
import ChatIcon from '@mui/icons-material/Chat';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import CommentIcon from '@mui/icons-material/Comment';
import LockIcon from '@mui/icons-material/Lock';
import { FormattedMessage, injectIntl } from 'react-intl';
import moment from 'moment';
import Api from '../../../services/api';
import { makeStyles, useTheme } from '@mui/styles';
import DOMPurify from 'dompurify';
import Api2Talk from 'src/services/api2talk';
import { renderToStaticMarkup } from 'react-dom/server';
import parse from 'html-react-parser';
import { getFileDownloadLink } from 'src/services/attachment.service';


// Detecta o tema atual baseado nas variáveis CSS
const detectTheme = () => {
  const primaryColor = getComputedStyle(document.documentElement)
    .getPropertyValue('--mexx-green-primary')
    .trim();

  const isBlueTheme = primaryColor.includes('2979ff') || primaryColor.includes('#2979ff');
  return isBlueTheme ? 'blue' : 'green';
};

// Cores do tema para campos personalizados
const getThemeColors = (themeName) => {
  if (themeName === 'blue') {
    return {
      primary: '#1E88E5',
      light: '#E3F2FD',
      dark: '#1565C0',
      border: '#BBDEFB',
      accent: '#0D47A1',
    };
  } else {
    return {
      primary: '#558B2F',
      light: '#F1F8E9',
      dark: '#33691E',
      border: '#DCEDC8',
      accent: '#1B5E20',
    };
  }
};

const useStyles = makeStyles((theme) => ({
  panelContainer: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  panelContent: {
    padding: theme.spacing(2.5),
    flex: 1,
    overflowY: 'auto',
    overflowX: 'hidden',
    display: 'block',
    minHeight: 0,
    backgroundColor: '#FFFFFF',
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2),
    },
    '&::-webkit-scrollbar': {
      width: '6px',
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: '#CFD8DC',
      borderRadius: '3px',
      '&:hover': {
        backgroundColor: '#B0BEC5',
      }
    }
  },
  infoSection: {
    marginBottom: theme.spacing(2),
  },
  label: {
    fontWeight: 600,
    marginBottom: theme.spacing(0.5),
    color: '#546E7A',
    fontSize: '0.8rem',
  },
  value: {
    marginBottom: theme.spacing(1),
    wordBreak: 'break-word',
    color: '#37474F',
    fontSize: '0.875rem',
  },
  messageItem: {
    marginBottom: theme.spacing(1),
    padding: theme.spacing(1.5),
    backgroundColor: '#FAFAFA',
    borderRadius: '8px',
    border: '1px solid #ECEFF1',
  },
  colorIndicator: {
    width: 12,
    height: 12,
    borderRadius: '50%',
    display: 'inline-block',
    marginRight: '6px',
  },
  descriptionBox: {
    padding: theme.spacing(2),
    backgroundColor: '#FAFAFA',
    borderRadius: '8px',
    marginTop: theme.spacing(1),
    border: '1px solid #ECEFF1',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    '& img': {
      maxWidth: '100%',
      height: 'auto',
      borderRadius: '4px',
    },
    '& a': {
      wordBreak: 'break-all',
    },
    '& pre, & code': {
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word',
    },
  },
  messagesContainer: {
    flex: 1,
    padding: theme.spacing(3),
    overflowY: 'auto',
    '&::-webkit-scrollbar': {
      width: '6px',
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: '#CBD5E0',
      borderRadius: '3px',
      '&:hover': {
        backgroundColor: '#A0AEC0',
      }
    }
  },
  messageGroup: {
    marginBottom: theme.spacing(3),
  },
  messageBubble: {
    padding: theme.spacing(1.75, 2.25),
    borderRadius: '8px',
    wordWrap: 'break-word',
    position: 'relative',
    minWidth: '150px',
    width: 'fit-content',
  },
  customerMessage: {
    backgroundColor: 'white',
    border: '1px solid #E2E8F0',
    boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
  },
  agentMessage: {
    backgroundColor: '#EBF8F2',
    border: '1px solid #C6F6D5',
    boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
  },
  messageHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  senderAvatar: {
    width: 24,
    height: 24,
    fontSize: '0.75rem',
  },
  senderName: {
    fontWeight: 600,
    fontSize: '0.8125rem',
    lineHeight: 1,
  },
  messageContent: {
    fontSize: '0.875rem',
    lineHeight: 1.5,
    color: '#2D3748',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  messageFooter: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: theme.spacing(1),
    marginTop: theme.spacing(0.75),
  },
  timestamp: {
    fontSize: '0.6875rem',
    color: '#718096',
    fontWeight: 500,
  },
  infoBar: {
    padding: theme.spacing(2, 3),
    backgroundColor: 'white',
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  infoLabel: {
    fontSize: '0.75rem',
    fontWeight: 600,
    color: '#718096',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: theme.spacing(0.5),
  },
  infoValue: {
    fontSize: '0.875rem',
    color: '#2D3748',
    fontWeight: 500,
  },
  statusBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    padding: theme.spacing(0.5, 1.25),
    borderRadius: '6px',
    fontSize: '0.75rem',
    fontWeight: 600,
  },
  attachmentCard: {
    marginTop: theme.spacing(1.25),
    padding: theme.spacing(1.25, 1.5),
    backgroundColor: 'rgba(0,0,0,0.02)',
    border: '1px solid rgba(0,0,0,0.06)',
    borderRadius: '6px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: 'rgba(0,0,0,0.04)',
      borderColor: theme.palette.primary.main,
      transform: 'translateX(2px)',
    },
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '400px',
    padding: theme.spacing(4),
  },
  emptyIcon: {
    fontSize: 64,
    color: '#CBD5E0',
    marginBottom: theme.spacing(2),
  },
  attachmentItem: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(1),
    cursor: 'pointer',
    border: '1px solid #ECEFF1',
    borderRadius: '8px',
    transition: 'all 0.2s ease',
    backgroundColor: '#FAFAFA',
    '&:hover': {
      backgroundColor: '#F5F5F5',
      borderColor: theme.palette.primary.main,
      transform: 'translateX(3px)',
    },
  },
  attachmentIcon: {
    marginRight: theme.spacing(1.5),
    color: theme.palette.primary.main,
    fontSize: '1.5rem',
  },
  attachmentInfo: {
    flex: 1,
  },
  panelFooter: {
    padding: theme.spacing(2, 3),
    borderTop: `1px solid ${theme.palette.divider}`,
    flexShrink: 0,
    backgroundColor: '#FAFAFA',
  },
  tabsContainer: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    marginTop: theme.spacing(2),
  },
  tabPanel: {
    padding: theme.spacing(2, 0),
  },
  actionItem: {
    padding: theme.spacing(1.75),
    backgroundColor: '#FAFAFA',
    borderRadius: '8px',
    marginBottom: theme.spacing(1.25),
    display: 'flex',
    border: '1px solid #ECEFF1',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: '#F5F5F5',
      transform: 'translateX(2px)',
    },
  },
  actionIndicator: {
    marginRight: '4px',
    minWidth: '4px',
    width: '4px',
    height: 'inherit',
    borderRadius: '4px',
  },
  actionContent: {
    flex: 1,
  },
  actionHeader: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  actionAuthor: {
    fontWeight: 600,
    color: '#37474F',
    margin: 0,
    fontSize: '0.875rem',
  },
  actionDate: {
    color: '#78909C',
    marginLeft: theme.spacing(1.5),
    fontSize: '0.8rem',
  },
  actionDescription: {
    marginTop: theme.spacing(1),
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    '& img': {
      maxWidth: '100%',
      height: 'auto',
    },
    '& a': {
      wordBreak: 'break-all',
    },
    '& pre, & code': {
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word',
    },
  },
  // Estilos para estado de acesso negado
  accessDeniedState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '400px',
    padding: theme.spacing(4),
    textAlign: 'center',
  },
  accessDeniedIcon: {
    fontSize: 80,
    color: '#EF5350',
    marginBottom: theme.spacing(3),
    opacity: 0.8,
  },
  accessDeniedTitle: {
    fontWeight: 600,
    color: '#37474F',
    marginBottom: theme.spacing(1.5),
  },
  accessDeniedMessage: {
    color: '#78909C',
    maxWidth: '400px',
    lineHeight: 1.6,
  },
}));

function RequesterHistoryDetailsModal({ open, onClose, item, intl, setMessage }) {
  const classes = useStyles();
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [details, setDetails] = useState(null);
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);
  const [currentTab, setCurrentTab] = useState(0);
  const [currentTheme, setCurrentTheme] = useState('green');
  const [accessDenied, setAccessDenied] = useState(false);
  const [accessDeniedMessage, setAccessDeniedMessage] = useState('');

  // Detecta tema atual
  useEffect(() => {
    const detected = detectTheme();
    setCurrentTheme(detected);

    const observer = new MutationObserver(() => {
      const newTheme = detectTheme();
      if (newTheme !== currentTheme) {
        setCurrentTheme(newTheme);
      }
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['style']
    });

    return () => observer.disconnect();
  }, [currentTheme]);

  const themeColors = getThemeColors(currentTheme);

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const getFile = async attachment => {
		setLoading(true)

		let url = await getFileDownloadLink(attachment.id)
		if(url &&  url.data && url.data.data && url.data.success){
      setLoading(false)
			window.open(`${url.data.data}`, '_blank');
		} else{
        setMessage(true, 'error', intl.formatMessage({ id: "file.not.found" }))
        setLoading(false)
    }
  }

  const configureDescription = (action) => {
    let reg = new RegExp(/\@\@{.*\|\@\|.*\}$$/gm);
    let testReg = reg.test(action.description.trim());

    if(action.description.trim().startsWith("<p> &lt;h") || action.description.trim().startsWith("<p>&lt;")) {
      let actionDescription = DOMPurify.sanitize(action.description);
      return renderToStaticMarkup(actionDescription);
    } else if(action.description.trim().startsWith("<div style=\"") || action.description.trim().startsWith("<div dir=") || action.description.trim().startsWith("<p>") || action.description.trim().startsWith("<h1>") || action.description.trim().startsWith("<h2>") || action.description.trim().startsWith("<h3>")) {
      return DOMPurify.sanitize(action.description);
    } else if(action.description.trim().endsWith(".webm") || action.description.trim().endsWith(".webm}")) {
      let descriptionSplice = action.description.split(" : ");
      if(descriptionSplice.length > 0 && descriptionSplice[0] && descriptionSplice[1]) {
        let newDescription = <p> {descriptionSplice[0].replace(intl.formatMessage({id:"message.sent"}), intl.formatMessage({id:"video.sent"}))} <br/> <video style={{ width: '310px', height: '310px' }} playsInline controls><source src={descriptionSplice[1]} type='video/webm'/></video> </p>;
        return renderToStaticMarkup(DOMPurify.sanitize(newDescription));
      }
    } else if(testReg) {
      let descriptionSplice = action.description.split(" : ");
      if(descriptionSplice.length > 0 && descriptionSplice[0] && descriptionSplice[1]) {
        let newAction = descriptionSplice[1].slice(3, -1);
        let newActionSplice = newAction.split("|@|");
        if(newActionSplice.length > 0 && newActionSplice[0] && newActionSplice[1]) {
          let title = newActionSplice[1];
          if(title.trim().endsWith("}")) title = title.slice(0, -1);
          if(action.description.trim().endsWith(".pdf") || action.description.trim().endsWith(".pdf}")) {
            let newDescription = <p> {descriptionSplice[0].replace(intl.formatMessage({id:"message.sent"}), intl.formatMessage({id:"document.sent"}))} <br/> <a href={newActionSplice[0]} target="_blank"> {title} </a> </p>;
            return renderToStaticMarkup(DOMPurify.sanitize(newDescription));
          } else {
            let newDescription = <p> {descriptionSplice[0].replace(intl.formatMessage({id:"message.sent"}), intl.formatMessage({id:"video.sent"}))} <br/> <a href={newActionSplice[0]} target="_blank"> {title} </a> </p>;
            return renderToStaticMarkup(DOMPurify.sanitize(newDescription));
          }
        }
      }
    } else if(action.description.trim().endsWith(".pdf") || action.description.trim().endsWith(".pdf}")) {
      let descriptionSplice = action.description.split(" : ");
      if(descriptionSplice.length > 0 && descriptionSplice[0] && descriptionSplice[1]){
        let newDescription = <p> {descriptionSplice[0].replace(intl.formatMessage({id:"message.sent"}), intl.formatMessage({id:"document.sent"}))} <br/> <a href={descriptionSplice[1]} target="_blank"> {descriptionSplice[1]} </a> </p>;
        return renderToStaticMarkup(DOMPurify.sanitize(newDescription));
      }
    } else if(action.description.trim().endsWith(".jpg") || action.description.trim().endsWith(".png") || action.description.trim().endsWith(".jpeg") || action.description.trim().endsWith(".jpg}") || action.description.trim().endsWith(".png}") || action.description.trim().endsWith(".jpeg}")) {
      let descriptionSplice = action.description.split(" : ");
      if(descriptionSplice.length > 0 && descriptionSplice[0] && descriptionSplice[1]) {
        let newDescription = <p> {descriptionSplice[0]} <br/> <a href={descriptionSplice[1]} target="_blank"> {descriptionSplice[1]} </a> </p>;
        return renderToStaticMarkup(DOMPurify.sanitize(newDescription));
      }
    } else if(action.description.trim().startsWith("<div>")) {
      return DOMPurify.sanitize(action.description);
    }

    return renderToStaticMarkup(<p>{DOMPurify.sanitize(action.description)}</p>);
  };

  useEffect(() => {
    if (item) {
      setAccessDenied(false);
      setAccessDeniedMessage('');
      setDetails(null);
      setCurrentTab(0);
      setDescriptionExpanded(false);
      loadDetails();
    }
  }, [item]);

  const loadDetails = async () => {
    if (!item) return;

    setLoading(true);

    try {
      let response;

      if (item.type === 'called' || item.type === 'Called' || item.calledId) {
        const calledId = item.id || item.calledId;
        response = await Api.get(`calleds/called-history/${calledId}`);
      } else if (item.type === 'chat' || item.type === 'Chat' || item.chatId) {
        const chatId = item.id || item.chatId;
        response = await Api2Talk.get(`/chat/get-by-id/history/${chatId}`);
      } else {
        const id = item.id;
        response = await Api.get(`/performance/calleds/consult/${id}`);
      }

      if (response && response.data) {
        if (response.data.accessDenied === true) {
          setAccessDenied(true);
          setAccessDeniedMessage(
            response.data.message ||
            intl.formatMessage({
              id: "access.denied.message",
              defaultMessage: "Você não possui permissão para visualizar os detalhes deste item."
            })
          );
          setDetails(null);
          return;
        }

        let data = response.data.success ? response.data.data : response.data;

        if (item.type === 'chat' || item.type === 'Chat' || item.chatId) {
          data = normalizeData(data);
        }

        setDetails(data || item);
      } else {
        // Handle error case
        const errorMessage = response?.response?.data?.errors
          ? response.response.data.errors[0]
          : intl.formatMessage({ id: "process.error" });

        if (setMessage) {
          setMessage(true, "error", errorMessage);
        }

        Api.kickoff(response);
        setDetails(item);
      }
    } catch (error) {
      console.error('Error loading details:', error);

      if (error?.response?.status === 403) {
        const responseData = error.response.data;
        setAccessDenied(true);
        setAccessDeniedMessage(
          responseData?.message ||
          responseData?.errors?.[0] ||
          intl.formatMessage({
            id: "access.denied.message",
            defaultMessage: "Você não possui permissão para visualizar os detalhes deste item."
          })
        );
        setDetails(null);
        return;
      }

      if (setMessage) {
        const errorMessage = error?.response?.data?.errors
          ? error.response.data.errors[0]
          : intl.formatMessage({ id: "process.error" });
        setMessage(true, "error", errorMessage);
      }

      Api.kickoff(error);
      setDetails(item);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    return moment(date).format('DD/MM/YYYY HH:mm');
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  const normalizeData = (data) => {
    if (!data) return data;

    const normalized = {
      id: data.id || data.Id,
      title: data.title || data.Title,
      active: data.active !== undefined ? data.active : data.Active,
      createdAt: data.createdAt || data.CreatedAt || data.createdat,
      customerName: data.customerName || data.CustomerName || data.customername,
      status: data.status || data.Status,
      chatMessages: data.chatMessages || data.ChatMessages || data.chatmessages || []
    };

    if (normalized.status) {
      normalized.status = {
        id: normalized.status.id || normalized.status.Id,
        name: normalized.status.name || normalized.status.Name,
        colorHex: normalized.status.colorHex || normalized.status.ColorHex || normalized.status.colorhex
      };
    }

    normalized.chatMessages = normalized.chatMessages.map(msg => ({
      id: msg.id || msg.Id,
      chatId: msg.chatId || msg.ChatId || msg.chatid,
      content: msg.content || msg.Content || msg.message || msg.Message,
      createdAt: msg.createdAt || msg.CreatedAt || msg.createdat,
      messageType: msg.messageType || msg.MessageType || msg.messagetype,
      senderName: msg.senderName || msg.SenderName || msg.sendername,
      messageOrigin: msg.messageOrigin || msg.MessageOrigin || msg.messageorigin,
      isFromAgent: msg.isFromAgent !== undefined ? msg.isFromAgent : msg.IsFromAgent,
      attachments: msg.attachments || msg.Attachments || msg.chatmessageattachments || []
    }));

    return normalized;
  };

  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const renderAccessDenied = () => {
    const isChat = item?.type === 'chat' || item?.type === 'Chat' || item?.chatId;
    const itemType = isChat
      ? intl.formatMessage({ id: "conversation", defaultMessage: "conversa" })
      : intl.formatMessage({ id: "called", defaultMessage: "chamado" });

    return (
      <Box className={classes.accessDeniedState}>
        <LockIcon className={classes.accessDeniedIcon} />
        <Typography variant="h6" className={classes.accessDeniedTitle}>
          <FormattedMessage
            id="access.denied.title"
            defaultMessage="Acesso Restrito"
          />
        </Typography>
        <Typography variant="body1" className={classes.accessDeniedMessage}>
          {accessDeniedMessage || (
            <FormattedMessage
              id="access.denied.default.message"
              defaultMessage="Você não possui permissão para visualizar os detalhes deste {itemType}. Entre em contato com o administrador se acredita que deveria ter acesso."
              values={{ itemType }}
            />
          )}
        </Typography>
        <Box sx={{ mt: 3 }}>
          <Button
            variant="outlined"
            color="primary"
            onClick={onClose}
            sx={{ minWidth: 120 }}
          >
            <FormattedMessage id="close" defaultMessage="Fechar" />
          </Button>
        </Box>
      </Box>
    );
  };

  const renderCalledDetails = () => {
    if (!details) return null;

    const sanitizedDescription = details.description
      ? DOMPurify.sanitize(details.description)
      : '';

    const assigned = details.organization?.organizationsettings?.displayassignedinfomode == 4
      ? details.techinicalteam?.name || ''
      : details.assigneduser?.name
        ? `${details.assigneduser.name}${details.assigneduser.email ? ` (${details.assigneduser.email})` : ''}`
        : '';

    return (
      <Grid container spacing={2}>
        {/* Prioridade e Status */}
        <Grid item xs={12}>
          <Box display="flex" gap={3} flexWrap="wrap" sx={{ mt: 2 }}>
            {details.priority && (
              <Box display="flex" alignItems="center">
                <Typography className={classes.label} variant="subtitle2" component="span">
                  <FormattedMessage id="priority" defaultMessage="Prioridade" />:
                </Typography>
                <Box ml={1} display="flex" alignItems="center">
                  {details.priority.colorhex && (
                    <span
                      className={classes.colorIndicator}
                      style={{ backgroundColor: details.priority.colorhex }}
                    />
                  )}
                  <Typography variant="body2">{details.priority.name}</Typography>
                </Box>
              </Box>
            )}
            {details.status && (
              <Box display="flex" alignItems="center">
                <Typography className={classes.label} variant="subtitle2" component="span">
                  <FormattedMessage id="status" defaultMessage="Status" />:
                </Typography>
                <Box ml={1} display="flex" alignItems="center">
                  {details.status.colorhex && (
                    <span
                      className={classes.colorIndicator}
                      style={{ backgroundColor: details.status.colorhex }}
                    />
                  )}
                  <Typography variant="body2">{details.status.name}</Typography>
                </Box>
              </Box>
            )}
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Divider />
        </Grid>

        {/* Descrição */}
        <Grid item xs={12}>
          <Accordion
            expanded={descriptionExpanded}
            onChange={() => setDescriptionExpanded(!descriptionExpanded)}
          >
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography className={classes.label} variant="subtitle2">
                <FormattedMessage id="description" defaultMessage="Descrição" />
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box
                className={classes.descriptionBox}
                sx={{
                  maxHeight: '120px',
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  '&::-webkit-scrollbar': {
                    width: '6px',
                  },
                  '&::-webkit-scrollbar-track': {
                    backgroundColor: '#e0e0e0',
                    borderRadius: '3px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: '#888',
                    borderRadius: '3px',
                    '&:hover': {
                      backgroundColor: '#555',
                    }
                  }
                }}
              >
                {sanitizedDescription ? (
                  <div dangerouslySetInnerHTML={{ __html: sanitizedDescription }} />
                ) : (
                  <Typography variant="body2" color="textSecondary">-</Typography>
                )}
              </Box>
            </AccordionDetails>
          </Accordion>
        </Grid>

        <Grid item xs={12}>
          <Divider />
        </Grid>

        {/* Categoria */}
        <Grid item xs={12}>
          <Typography className={classes.label} variant="subtitle2">
            <FormattedMessage id="category" defaultMessage="Categoria" />
          </Typography>
          <Typography className={classes.value} variant="body2">
            {details.organizationcategory?.parentname || details.organizationcategory?.name || '-'}
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <Divider />
        </Grid>

        {/* Organização */}
        <Grid item xs={12} md={6}>
          <Typography className={classes.label} variant="subtitle2">
            <FormattedMessage id="organization" defaultMessage="Organização" />
          </Typography>
          <Typography className={classes.value} variant="body2">
            {details.organization?.name || '-'}
          </Typography>
        </Grid>

        {/* Solicitante */}
        <Grid item xs={12} md={6}>
          <Typography className={classes.label} variant="subtitle2">
            <FormattedMessage id="requestuser" defaultMessage="Solicitante" />
          </Typography>
          <Typography className={classes.value} variant="body2">
            {details.requestinguser?.name
              ? `${details.requestinguser.name}${details.requestinguser.email ? ` (${details.requestinguser.email})` : ''}`
              : '-'}
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <Divider />
        </Grid>

        {/* Time de Atendimento */}
        <Grid item xs={12} md={6}>
          <Typography className={classes.label} variant="subtitle2">
            <FormattedMessage id="attendance.team" defaultMessage="Time de Atendimento" />
          </Typography>
          <Typography className={classes.value} variant="body2">
            {details.techinicalteam?.name || '-'}
          </Typography>
        </Grid>

        {/* Responsável */}
        <Grid item xs={12} md={6}>
          <Typography className={classes.label} variant="subtitle2">
            <FormattedMessage id="assigned" defaultMessage="Responsável" />
          </Typography>
          <Typography className={classes.value} variant="body2">
            {assigned || '-'}
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <Divider />
        </Grid>

        {/* Tipo */}
        <Grid item xs={12} md={6}>
          <Typography className={classes.label} variant="subtitle2">
            <FormattedMessage id="type" defaultMessage="Tipo" />
          </Typography>
          <Typography className={classes.value} variant="body2">
            {details.type?.name || '-'}
          </Typography>
        </Grid>

        {/* Endereço */}
        <Grid item xs={12} md={6}>
          <Typography className={classes.label} variant="subtitle2">
            <FormattedMessage id="address" defaultMessage="Endereço" />
          </Typography>
          <Typography className={classes.value} variant="body2">
            {details.address ? (
              <>
                {details.address.name && <div>{details.address.name}</div>}
                <div>
                  {[
                    details.address.street,
                    details.address.number && `, ${details.address.number}`,
                    details.address.neighborhood,
                    details.address.city && `- ${details.address.city}`,
                    details.address.state && `, ${details.address.state}`,
                    details.address.country && `- ${details.address.country}`,
                  ].filter(Boolean).join(' ')}
                </div>
              </>
            ) : '-'}
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <Divider />
        </Grid>

        {/* Datas e Informações Adicionais */}
        <Grid item xs={12} md={6}>
          <Typography className={classes.label} variant="subtitle2">
            <FormattedMessage id="created.via" defaultMessage="Criado via" />
          </Typography>
          <Typography className={classes.value} variant="body2">
            {details.originrequest?.name || '-'} {details.openingdate && `em ${formatDate(details.openingdate)}`}
          </Typography>
        </Grid>

        <Grid item xs={12} md={6}>
          <Typography className={classes.label} variant="subtitle2">
            <FormattedMessage id="security.token" defaultMessage="Token de Segurança" />
          </Typography>
          <Typography className={classes.value} variant="body2" fontFamily="monospace">
            {details.securitypassword || '-'}
          </Typography>
        </Grid>

        <Grid item xs={12} md={6}>
          <Typography className={classes.label} variant="subtitle2">
            <FormattedMessage id="solution.forecast" defaultMessage="Previsão de Solução" />
          </Typography>
          <Typography className={classes.value} variant="body2">
            {details.duedate ? formatDate(details.duedate) : '-'}
          </Typography>
        </Grid>

        {details.archived && (
          <Grid item xs={12} md={6}>
            <Typography className={classes.label} variant="subtitle2" color="warning.main">
              <FormattedMessage id="status" defaultMessage="Status" />
            </Typography>
            <Typography className={classes.value} variant="body2" fontStyle="italic" color="warning.main">
              (Arquivado)
            </Typography>
          </Grid>
        )}

        {/* Campos Personalizados */}
        {details.calledorganizationfields && details.calledorganizationfields.length > 0 && (
          <>
            <Grid item xs={12}>
              <Divider sx={{ mt: 2 }} />
            </Grid>
            <Grid item xs={12}>
              <Typography className={classes.label} variant="subtitle2" sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                <FormattedMessage id="custom.fields" defaultMessage="Campos Personalizados" />
              </Typography>
            </Grid>
            {details.calledorganizationfields.map((field, index) => {
              const fieldValue = field.organizationfieldoptionvalues?.name || field.value || '-';
              return (
                <Grid item xs={12} md={6} key={field.id || index}>
                  <Box
                    sx={{
                      p: 2,
                      backgroundColor: themeColors.light,
                      borderRadius: '10px',
                      borderLeft: `4px solid ${themeColors.primary}`,
                      transition: 'all 0.2s ease',
                      height: '100%',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: `0 4px 12px ${themeColors.primary}20`,
                      },
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 600,
                        color: themeColors.dark,
                        textTransform: 'uppercase',
                        fontSize: '0.68rem',
                        letterSpacing: '0.4px',
                        display: 'block',
                        mb: 0.75,
                      }}
                    >
                      {field.organizationfield?.name || 'Campo'}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        color: '#37474F',
                        wordBreak: 'break-word',
                        lineHeight: 1.5,
                      }}
                    >
                      {fieldValue}
                    </Typography>
                  </Box>
                </Grid>
              );
            })}
          </>
        )}



        {/* Mensagens/Interações do Chamado */}
        {details.calledmessages && details.calledmessages.length > 0 && (
          <>
            <Grid item xs={12}>
              <Divider sx={{ mt: 2 }} />
            </Grid>
            <Grid item xs={12}>
              <Typography className={classes.label} variant="subtitle2" sx={{ mb: 1.5 }}>
                <FormattedMessage id="messages" defaultMessage="Mensagens" /> ({details.calledmessages.length})
              </Typography>
              <List>
                {details.calledmessages.map((message, index) => (
                  <ListItem
                    key={index}
                    className={classes.messageItem}
                    sx={{ flexDirection: 'column', alignItems: 'flex-start' }}
                  >
                    <Box display="flex" justifyContent="space-between" width="100%" mb={1}>
                      <Typography variant="caption" fontWeight={600} color="primary">
                        {message.user?.name || 'Usuário'}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {formatDate(message.createdat)}
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {message.message || '-'}
                    </Typography>
                  </ListItem>
                ))}
              </List>
            </Grid>
          </>
        )}

        {/* Tabs de Anexos e Ações - Sempre visíveis */}
        <Grid item xs={12}>
          <Divider sx={{ mt: 2 }} />
        </Grid>
        <Grid item xs={12}>
          <Box className={classes.tabsContainer}>
            <Tabs value={currentTab} onChange={handleTabChange} sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tab
                icon={<AttachFileIcon />}
                iconPosition="start"
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <FormattedMessage id="attachments" defaultMessage="Anexos" />
                    {details.calledattachments && details.calledattachments.length > 0 && (
                      <Chip
                        label={details.calledattachments.length}
                        size="small"
                        sx={{ height: 18, minWidth: 18, fontSize: '0.7rem' }}
                      />
                    )}
                  </Box>
                }
                sx={{ textTransform: 'none', minHeight: 48 }}
              />
              <Tab
                icon={<CommentIcon />}
                iconPosition="start"
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <FormattedMessage id="actions" defaultMessage="Ações" />
                    {details.accompaniments && details.accompaniments.length > 0 && (
                      <Chip
                        label={details.accompaniments.length}
                        size="small"
                        sx={{ height: 18, minWidth: 18, fontSize: '0.7rem' }}
                      />
                    )}
                  </Box>
                }
                sx={{ textTransform: 'none', minHeight: 48 }}
              />
            </Tabs>
          </Box>

          {/* Tab Panel - Anexos */}
          {currentTab === 0 && (
            <Box className={classes.tabPanel}>
              {details.calledattachments && details.calledattachments.length > 0 ? (
                <Grid container spacing={1}>
                  {details.calledattachments.map((calledAttachment, index) => {
                    const attachment = calledAttachment.attachment;
                    if (!attachment) return null;

                    return (
                      <Grid item xs={12} key={index}>
                        <Paper
                          className={classes.attachmentItem}
                          elevation={0}
                          onClick={() => getFile(attachment)}
                        >
                          <AttachFileIcon className={classes.attachmentIcon} />
                          <Box className={classes.attachmentInfo}>
                            <Typography variant="body2" fontWeight={500}>
                              {attachment.name || attachment.filename || attachment.description || `Anexo ${index + 1}`}
                            </Typography>
                            {attachment.size && (
                              <Typography variant="caption" color="textSecondary">
                                {formatFileSize(attachment.size)}
                              </Typography>
                            )}
                          </Box>
                        </Paper>
                      </Grid>
                    );
                  })}
                </Grid>
              ) : (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="100px">
                  <Typography variant="body2" color="textSecondary">
                    <FormattedMessage id="no.attachments" defaultMessage="Nenhum anexo disponível" />
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {/* Tab Panel - Ações */}
          {currentTab === 1 && (
            <Box className={classes.tabPanel}>
              {details.accompaniments && details.accompaniments.length > 0 ? (
                <Grid container spacing={1}>
                  {details.accompaniments.map((action, index) => (
                    <Grid item xs={12} key={index}>
                      <Box className={classes.actionItem}>
                        <Box
                          className={classes.actionIndicator}

                        />
                        <Box className={classes.actionContent}>
                        <Box
                          className={classes.actionHeader}
                          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                        >
                          <Typography className={classes.actionAuthor}>
                            {action.createduserdata?.name || 'Usuário'}
                          </Typography>

                          <Typography
                            sx={{ opacity: 0.6 }}
                          >
                            —
                          </Typography>

                          <Typography className={classes.actionDate}>
                            <FormattedMessage id="date" defaultMessage="Data" />: {formatDate(action.createdat)}
                          </Typography>

                          {action.privacy !== undefined && (
                            <Typography
                              variant="caption"
                              sx={{
                                ml: 1.5,
                                fontStyle: 'italic',

                              }}
                            >
                              {action.privacy
                                ? intl.formatMessage({ id: 'private.action' })
                                : intl.formatMessage({ id: 'public.action' })}
                            </Typography>
                          )}
                        </Box>
                          <Box className={classes.actionDescription}>
                            {parse(configureDescription(action))}
                          </Box>
                          {action.accompanimentattachments && action.accompanimentattachments.length > 0 && (
                            <Box sx={{ mt: 1.5, pl: 2 }}>
                              {action.accompanimentattachments.map((attItem, attIndex) => (
                                <Box
                                  key={attIndex}
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    mb: 0.5
                                  }}
                                >
                                  <AttachFileIcon sx={{ fontSize: 16, mr: 0.5 }} />
                                  <Link
                                    component="button"
                                    variant="body2"
                                    onClick={() => getFile(attItem.attachment)}
                                  >
                                    {attItem.attachment?.description || `Anexo ${attIndex + 1}`}
                                  </Link>
                                </Box>
                              ))}
                            </Box>
                          )}
                        </Box>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="100px">
                  <Typography variant="body2" color="textSecondary">
                    <FormattedMessage id="no.actions" defaultMessage="Nenhuma ação registrada" />
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </Grid>
      </Grid>
    );
  };

  const renderConversationDetails = () => {
    if (!details) return null;

    const messages = details.chatMessages || [];

    return (
      <>
        {/* Information Bar */}
        <Box className={classes.infoBar}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={4}>
              <Typography className={classes.infoLabel}>
                <FormattedMessage id="conversation.id" defaultMessage="ID da Conversa" />
              </Typography>
              <Typography className={classes.infoValue}>
                #{details.id}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Typography className={classes.infoLabel}>
                <FormattedMessage id="customer" defaultMessage="Cliente" />
              </Typography>
              <Typography className={classes.infoValue}>
                {details.customerName || '-'}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Typography className={classes.infoLabel}>
                <FormattedMessage id="status" defaultMessage="Status" />
              </Typography>
              <Box
                className={classes.statusBadge}
                sx={{
                  backgroundColor: details.status?.colorHex ? `${details.status.colorHex}15` : '#F7FAFC',
                  color: details.status?.colorHex || '#2D3748',
                }}
              >
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    backgroundColor: details.status?.colorHex || '#CBD5E0',
                  }}
                />
                {details.status?.name || '-'}
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Messages Container */}
        {messages && messages.length > 0 ? (
          <Box className={classes.messagesContainer}>
            <Stack spacing={2}>
              {messages.map((message, index) => {
                const isFromAgent = message.messageOrigin === 4 ||
                                   message.messageOrigin === 1 ||
                                   message.messageOrigin === 5;

                return (
                  <Box
                    key={message.id || index}
                    sx={{
                      display: 'flex',
                      justifyContent: isFromAgent ? 'flex-end' : 'flex-start',
                      width: '100%',
                    }}
                  >
                    <Box className={classes.messageGroup} sx={{ maxWidth: '85%' }}>
                      {/* Message Bubble */}
                      <Paper
                        elevation={0}
                        className={`${classes.messageBubble} ${
                          isFromAgent ? classes.agentMessage : classes.customerMessage
                        }`}
                      >
                        {/* Message Header */}
                        <Box className={classes.messageHeader}>
                          <Avatar
                            className={classes.senderAvatar}
                            sx={{
                              bgcolor: isFromAgent ? '#38A169' : '#3182CE',
                            }}
                          >
                            {isFromAgent ? (
                              <SupportAgentIcon sx={{ fontSize: 14 }} />
                            ) : (
                              <PersonOutlineIcon sx={{ fontSize: 14 }} />
                            )}
                          </Avatar>
                          <Typography
                            className={classes.senderName}

                          >
                            {message.senderName || (isFromAgent ? 'Atendente' : 'Cliente')}
                          </Typography>
                        </Box>

                        {/* Message Content */}
                        <Typography className={classes.messageContent}>
                          {message.content || '-'}
                        </Typography>

                        {/* Attachments */}
                        {message.attachments && message.attachments.length > 0 && (
                          <Stack spacing={0.75} sx={{ mt: 1.25 }}>
                            {message.attachments.map((msgAttachment, attIndex) => {
                              const attachment = msgAttachment.attachment;
                              if (!attachment) return null;

                              return (
                                <Box
                                  key={attIndex}
                                  className={classes.attachmentCard}
                                  onClick={() => {
                                    if (attachment.path) {
                                      window.open(attachment.path, '_blank');
                                    }
                                  }}
                                >
                                  <Box display="flex" alignItems="center" gap={1}>
                                    <AttachFileIcon sx={{ fontSize: 16, color: 'action.active' }} />
                                    <Typography variant="caption" sx={{ flex: 1 }}>
                                      {attachment.description || `Anexo ${attIndex + 1}`}
                                    </Typography>
                                  </Box>
                                </Box>
                              );
                            })}
                          </Stack>
                        )}

                        {/* Message Footer */}
                        <Box className={classes.messageFooter}>
                          {message.messageType && message.messageType !== 'text' && (
                            <Chip
                              label={message.messageType}
                              size="small"
                              sx={{
                                height: 18,
                                fontSize: '0.65rem',
                                fontWeight: 600,
                              }}
                            />
                          )}
                          <Typography className={classes.timestamp}>
                            {formatDate(message.createdAt).split(' ')[1]}
                          </Typography>
                        </Box>
                      </Paper>
                    </Box>
                  </Box>
                );
              })}
            </Stack>
          </Box>
        ) : (
          <Box className={classes.emptyState}>
            <ChatIcon className={classes.emptyIcon} />
            <Typography variant="h6" color="textSecondary" gutterBottom>
              <FormattedMessage id="no.messages" defaultMessage="Nenhuma mensagem encontrada" />
            </Typography>
            <Typography variant="body2" color="textSecondary">
              <FormattedMessage
                id="no.messages.description"
                defaultMessage="Não há mensagens para exibir nesta conversa"
              />
            </Typography>
          </Box>
        )}
      </>
    );
  };

  const isChat = item ? (item.type === 'chat' || item.type === 'Chat' || item.chatId) : false;

  const renderMainContent = () => (
    <Box className={classes.panelContent}>
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
          <CircularProgress />
        </Box>
      ) : accessDenied ? (
        renderAccessDenied()
      ) : !item ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
          <Typography variant="body2" color="textSecondary">
            <FormattedMessage id="no.data" defaultMessage="Nenhum dado disponível" />
          </Typography>
        </Box>
      ) : isChat ? (
        renderConversationDetails()
      ) : (
        renderCalledDetails()
      )}
    </Box>
  );

  // Quando open é passado (agent-workspace), renderiza como Dialog central
  // Quando open não é passado (sidepanel), renderiza inline
  if (open !== undefined) {
    return (
      <Dialog
        fullWidth
        maxWidth="lg"
        open={open}
        onClose={onClose}
        aria-labelledby="details-modal-title"
      >
        <DialogTitle id="details-modal-title" style={{ backgroundColor: '#f5f5f5', borderBottom: '1px solid #e0e0e0', paddingBottom: 12 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={2}>
              <Typography variant="h6" style={{ fontWeight: 600 }}>
                {isChat
                  ? <FormattedMessage id="conversation.details" defaultMessage="Detalhes da Conversa" />
                  : <FormattedMessage id="called.details" defaultMessage="Detalhes do Chamado" />
                }
              </Typography>
              {item && (
                <Chip
                  label={`#${item.id || item.calledId || item.chatId || ''}`}
                  color="primary"
                  size="small"
                  style={{ fontWeight: 600 }}
                />
              )}
            </Box>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0, overflow: 'hidden' }}>
          <div style={{ overflowY: 'auto', maxHeight: 'calc(80vh - 140px)', padding: 0 }}>
            {renderMainContent()}
          </div>
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid #e0e0e0', px: 3, py: 1.5 }}>
          <Button onClick={onClose} variant="contained" color="primary">
            <FormattedMessage id="close" defaultMessage="Fechar" />
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  // Modo inline (sidepanel) — renderiza apenas o conteúdo com scroll vertical
  return (
    <div style={{ overflowY: 'auto', height: '100%' }}>
      {renderMainContent()}
    </div>
  );
}

export default injectIntl(RequesterHistoryDetailsModal);
